<template>
  <div>
    <transition name="fast-fade">
      <div class="property-modal" :class="{ 'has-features': showFeatures }" v-if="property._id">
        <div class="property-modal__mask" @click.stop="closeModal"></div>

        <div class="property-modal__inner">
          <div>
            <div v-if="styles['general']['show-on-hold-graphic'] && property.status === 'on-hold'" class="property-modal__status" :class="`property-modal__status--${property.status}`"></div>
            <div v-if="styles['general']['show-artist-impression-text']" class="property-modal__note">
              Artist impression only
            </div>
            <div class="property-modal__gallery">
              <img class="property-modal__overlay-image" v-if="overlayImageUrl" :src="overlayImageUrl">
              <img v-if="activeImage" :src="`${activeImage}`" :key="activeImage" :class="{'touch': imageIndex === images.length - 1}" @click="openVirtualTour">
              <video v-if="activeVideo" :src="`${activeVideo}`" loop muted autoplay disablePictureInPicture/>

              <div class="property-modal__controls">
                <div v-if="images.length > 1" class="prev">
                  <button 
                    class="btn-circle touch"
                    :class="{ 'inactive': imageIndex <= 0 }" 
                    @click="imageIndex > 0 ? imageIndex-- : null"
                  >
                    <fa :icon="['far', 'chevron-left']" />
                  </button>
                </div>
                <div v-if="images.length > 1" class="next">
                  <button 
                    class="btn-circle touch"
                    :class="{ 'inactive': imageIndex >= images.length - 1 }"
                    @click="imageIndex < images.length - 1 ? imageIndex++ : null"
                  >
                    <fa :icon="['far', 'chevron-right']" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="property-modal__info">
            <template v-if="styles['general']['show-favourites'] === true && !isHouse && !property.isDisplayHome">
              <button
                v-if="!isFavourited(property._id)"
                class="btn-circle btn-circle--lime touch"
                @click="addToFavourites(property)"
              >
                <fa :icon="['far', 'heart']" />
              </button>
  
              <button
                v-if="isFavourited(property._id)"
                class="btn-circle btn-circle--red touch"
                @click="removeFromFavourites(property)"
              >
                <fa :icon="['far', 'trash']" />
              </button>
            </template>
            
            <div class="detail-wrapper">
              <div v-if="isDisplayHome" class="details">
                <div v-if="property.dwelling && property.dwelling.builderLabel" class="detail">
                  <div class="detail__label">Builder</div>
                  <div class="detail__value">{{ property.dwelling.builderLabel }}</div>
                </div>                 
                <div class="detail">
                  <div class="detail__label">{{ property.labelTitle }}</div>
                  <div class="detail__value">{{ property.label }}</div>
                </div>                         
                <div v-if="property.dwelling && property.dwelling.size" class="detail">
                  <div class="detail__label">House Size</div>
                  <div class="detail__value">{{ property.dwelling.size }}m<sup>2</sup></div>
                </div>
                <div v-if="property.dwelling && property.dwelling.beds" class="detail">
                  <div class="detail__label">Beds</div>
                  <div class="detail__value">{{ property.dwelling.beds }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.baths" class="detail">
                  <div class="detail__label">Baths</div>
                  <div class="detail__value">{{ property.dwelling.baths }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.cars" class="detail">
                  <div class="detail__label">Cars</div>
                  <div class="detail__value">{{ property.dwelling.cars }}</div>
                </div>      
              </div>

              <div v-else-if="isTurnkey" class="details">
                <div class="detail">
                  <div class="detail__label">{{ property.labelTitle }}</div>
                  <div class="detail__value">{{ property.label }}</div>
                </div>

                <div v-if="property.dwelling && property.dwelling.houseDesign || property.dwelling.displayName || property.dwelling.label" class="detail">
                  <div class="detail__label">House Design</div>
                  <div class="detail__value">{{ property.dwelling.houseDesign || property.dwelling.displayName || property.dwelling.label }}</div>
                </div>                       

                <div v-if="property.dwelling && property.dwelling.builderLabel" class="detail">
                  <div class="detail__label">Builder</div>
                  <div class="detail__value">{{ property.dwelling.builderLabel }}</div>
                </div>
                
                <div v-if="styles['general']['show_price'] && property.price && (property.status !== 'sold' || (property.status === 'sold' && styles['general']['show-sold-price']))" class="detail">
                  <div class="detail__label">{{ property.priceLabel }}</div>
                  <div class="detail__value">{{ property.price }}</div>
                </div>  
                       
                <div v-if="property.dwelling && property.dwelling.size" class="detail">
                  <div class="detail__label">House Size</div>
                  <div class="detail__value">{{ property.dwelling.size }}m<sup>2</sup></div>
                </div>

                <div v-if="property.dwelling && property.dwelling.beds" class="detail">
                  <div class="detail__label">Beds</div>
                  <div class="detail__value">{{ property.dwelling.beds }}</div>
                </div>

                <div v-if="property.dwelling && property.dwelling.baths" class="detail">
                  <div class="detail__label">Baths</div>
                  <div class="detail__value">{{ property.dwelling.baths }}</div>
                </div>

                <div v-if="property.dwelling && property.dwelling.cars" class="detail">
                  <div class="detail__label">Cars</div>
                  <div class="detail__value">{{ property.dwelling.cars }}</div>
                </div>           
              </div>

              <div v-else class="details">
                <div class="detail">
                  <div class="detail__label">{{ property.labelTitle }}</div>
                  <div class="detail__value">{{ property.label }}</div>
                </div>                
                <div v-if="property.dwelling && property.dwelling.builderLabel" class="detail">
                  <div class="detail__label">Builder</div>
                  <div class="detail__value">{{ property.dwelling.builderLabel }}</div>
                </div>                 
                <div v-if="false && property.dwelling && property.dwelling.houseDesign" class="detail">
                  <div class="detail__label">House Design</div>
                  <div class="detail__value">{{ property.dwelling.houseDesign }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.label" class="detail">
                  <div class="detail__label">Builder Design</div>
                  <div class="detail__value">{{ property.dwelling.label }}</div>
                </div>                            
                <div v-if="!property.isDisplayHome && property.landSize" class="detail">
                  <div class="detail__label">Land Size</div>
                  <div class="detail__value">{{ property.landSize }}m<sup>2</sup></div>
                </div>
                <div v-if="property.dwelling && property.dwelling.size" class="detail">
                  <div class="detail__label">House Size</div>
                  <div class="detail__value">{{ property.dwelling.size }}m<sup>2</sup></div>
                </div>

                <div v-if="property.dwelling && property.dwelling.beds" class="detail">
                  <div class="detail__label">Beds</div>
                  <div class="detail__value">{{ property.dwelling.beds }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.baths" class="detail">
                  <div class="detail__label">Baths</div>
                  <div class="detail__value">{{ property.dwelling.baths }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.cars" class="detail">
                  <div class="detail__label">Cars</div>
                  <div class="detail__value">{{ property.dwelling.cars }}</div>
                </div>
                <div v-if="styles['general']['show_price'] && property.price && (property.status !== 'sold' || (property.status === 'sold' && styles['general']['show-sold-price']))" class="detail">
                  <div class="detail__label">{{ property.priceLabel }}</div>
                  <div class="detail__value">{{ property.price }}</div>
                </div>              
              </div>
              <div class="spacer"></div>
              <div class="property-modal__buttons">
                <button
                  v-for="iframe in property.iframes"
                  class="touch btn btn--auto"
                  :key="iframe.label"
                  @click="$store.commit('actions/SET_IFRAME_URL', iframe.url)"
                >
                  <div v-if="iframe.icon" class="btn__icon"><fa :icon="['fas', iframe.icon]"/></div>
                  <div>{{ iframe.label }}</div>
                </button>
                <div v-if="property.associatedPackages && property.associatedPackages.length" class="associated-packages">
                  <button class="touch btn btn--auto" @click="viewPackages">
                    <div class="btn__icon"><fa :icon="['fas', 'home']"/></div>
                    <div>{{ config.property_modal.associated_packages_button_label || 'View Packages' }}</div>
                  </button>
                </div>
                <div v-if="property.associatedLotPath" class="associated-packages">
                  <router-link class="touch btn btn--auto" :to="property.associatedLotPath">
                    <div class="btn__icon"><fa :icon="['fas', 'map-marked-alt']"/></div>
                    <div>View Lot</div>
                  </router-link>
                </div>
                <div v-if="property.suitableDwellings && property.suitableDwellings.length" class="associated-packages">
                  <router-link class="touch btn btn--auto" :to="`/${activeCategory}/live/dwellings?lotId=${property._id}`">
                    <div class="btn__icon"><fa :icon="['fas', 'home']"/></div>
                    <div>{{ config.property_modal.suitable_dwellings_button_label }}</div>
                  </router-link>
                </div>              
                <div class="facade">
                  <div v-if="facadeImages.length > 1">
                    <button class="touch btn btn--auto" @click="viewFacades">
                      <span>View Facades</span>
                    </button>
                  </div>
                </div>
              </div>
              <div class="close">
                <button class="btn-circle touch" @click="closeModal">
                  <fa :icon="['far', 'times']" />
                </button>
              </div>
            </div>
          </div>

          <div v-if="showFeatures" class="property-modal__features">
            <div v-if="property.description" class="feature-heading">Description</div>
            <div v-if="property.description" class="feature" v-html="property.description" />
            <div v-if="property.features" class="feature-heading">Features</div>
            <div v-if="property.features" class="feature-list" v-html="property.features" />
            <div v-if="property.isDisplayHome && property.displayHomeHours" class="feature-heading">Opening Hours</div>
            <div v-if="property.isDisplayHome && property.displayHomeHours" class="feature" v-html="property.displayHomeHours" />
            <div v-if="property.dwelling && (property.dwelling.builderLabel || property.dwelling.builderDescription)" class="feature-heading">Builder</div>
            <!-- <div v-if="property.isDisplayHome && property.dwelling.builderLabel && property.dwelling.builderDescription" class="feature" v-html="property.dwelling.builderLabel" /> -->
            <img class="property-modal__builder-logo" v-if="property.dwelling && property.dwelling.builderImage" :src="$store.state.assetsPath + property.dwelling.builderImage"/>
            <div v-if="property.dwelling && property.dwelling.builderLabel && property.dwelling.builderDescription" class="feature" v-html="property.dwelling.builderDescription" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import isEmpty from 'lodash/isEmpty'

export default {
  props: ['property'],
  data () {
    return {
      imageIndex: 0,
      images: []
    }
  },

  computed: {
    ...mapGetters({
      activeCategory: 'activeCategory',
      config: 'styleTemplate',
      packages: 'property/livingOptionsPackages',
      dwellings: 'property/livingOptionsDwellings',
      properties: 'property/properties',
      activeProperty: 'property/activeProperty',
      styles: 'styleTemplate'
    }),

    activeSubsectionId () {
      return this.$store.state.route.query.activeSubsectionId
    },

    activeImage () {
      const activeAsset = this.images.length ? this.images[this.imageIndex] : ''
      const ext = activeAsset && activeAsset.split('.').pop().toLowerCase()
      if (ext !== 'mp4' && ext !== 'mov') {
        return activeAsset
      } else {
        return null
      }
    },

    activeVideo () {
      const activeAsset = this.images.length ? this.images[this.imageIndex] : ''
      const ext = activeAsset && activeAsset.split('.').pop().toLowerCase()
      if (ext === 'mp4' || ext === 'mov') {
        return activeAsset
      } else {
        return null
      }
    },

    env () {
      return process.env
    },

    favouriteIds () {
      return this.$store.state.favourites.favouriteIds
    },

    activePackage () {
      if (this.isPackage) {
        const packageId = this.$store.state.route.params.packageId

        return this.packages.find(_package => {
          return _package._id === packageId
        })
      }

      return {}
    },

    isDisplayHome () {
      return this.property.type === 'display-home'
    },

    isTurnkey () {
      return this.property.type === 'turnkey'
    },

    isHouse () {
      return this.$store.state.route.name === 'dwelling-modal'
    },

    isPackage () {
      return this.$store.state.route.name === 'package-modal'
    },

    activeDwelling () {
      if (this.isHouse) {
        const dwellingId = this.$store.state.route.params.dwellingId

        return this.dwellings.find(dwelling => {
          return dwelling._id === dwellingId
        })
      }

      return null
    },

    facadeImages () {
      if (this.isHouse) { return this.activeDwelling.facadeImages }
      if (this.isPackage) { return this.activePackage.dwelling.facadeImages }

      return []
    },

    overlayImageUrl () {
      if (!this.property.dwelling || !this.property.dwelling.overlayImage) return false
      return this.$store.state.assetsPath + this.property.dwelling.overlayImage
    },

    showFeatures () {
      return this.property.description || this.property.features || (this.property.dwelling && this.property.dwelling.builderDescription) || (this.property.isDisplayHome && this.property.displayHomeHours)
    },

    virtualTourUrl () {
      if (this.activePackage.virtualTourUrl) {
        return this.activePackage.virtualTourUrl
      }

      if (this.activePackage.dwelling && this.activePackage.dwelling.virtualTourUrl) {
        return this.activePackage.dwelling.virtualTourUrl
      }

      if (this.activeProperty && this.activeProperty.dwelling && this.activeProperty.dwelling.virtualTourUrl) {
        return this.activeProperty.dwelling.virtualTourUrl
      }

      return ''
    },

    activeAssetRatio () {
      if (!this.activeImage) {
        return 16 / 9
      }

      let width = 4
      let height = 3

      const img = new Image()

      img.onload
      img.src = this.activeImage

      if (img.width && img.height) {
        width = img.width
        height = img.height
      }

      return width / height
    },
  },

  watch: {
    property (newProperty) {
      if (!isEmpty(newProperty)) {
        const assets = newProperty.images.map(image => {
          return this.$store.state.assetsPath + image
        })

        if (this.virtualTourUrl) {
          this.images = [...assets, '/assets/virtual-tour-slide.jpg']
        } else {
          this.images = assets
        }
      }
    }
  },

  methods: {
    addToFavourites (livingOption) {
      this.$store.commit('favourites/ADD_TO_FAVOURITES', livingOption._id)
    },

    closeModal () {
      this.imageIndex = 0

      const routeName = this.$store.state.route.name
      const category = this.$store.getters.activeCategory
      const lotId = this.$store.state.route.query.lotId
      const queryString = lotId ? `?lotId=${lotId}` : ''

      if (routeName === 'package-modal') {
        this.$router.push(`/${category}/live/packages${queryString}`)
      }

      if (routeName === 'dwelling-modal') {
        this.$router.push(`/${category}/live/dwellings${queryString}`)
      }

      if (routeName === 'turnkey-modal') {
        this.$router.push(`/${category}/live/turnkeys${queryString}`)
      }

      if (routeName === 'favourite-modal') {
        this.$router.push(`/${category}/favourites?p=${this.favouriteIds.toString()}`)
      }

      if (routeName === 'live-display-homes-property') {
        this.$router.push(`/${category}/live/display-homes${queryString}`)
      }
      
      if (routeName === 'property') {
        let currentPath = this.$route.path.split("property")[0]
        currentPath = currentPath.substr(0, currentPath.length - 1)

        this.$router.push(currentPath + `?activeSubsectionId=${this.activeSubsectionId}`)
      }
    },

    isFavourited (livingOptionId) {
      let isFavourited = false
      this.favouriteIds.forEach(favouriteId => {
        if (favouriteId === livingOptionId) {
          isFavourited = true
        }
      })

      return isFavourited
    },

    removeFromFavourites (livingOption) {
      this.$store.commit('favourites/REMOVE_FROM_FAVOURITES', livingOption._id)
    },

    viewPackages () {
      this.$router.push(`/${this.activeCategory}/live/packages?lotId=${this.property._id}`)
    },

    openVirtualTour () {
      if (this.virtualTourUrl) {
        this.$store.commit('property/SET_ACTIVE_VIRTUAL_TOUR_URL', this.virtualTourUrl)
      }
    },

    viewFacades () {
      this.$store.commit('actions/SET_SHOW_ASSET_PAYLOAD', {payload: this.facadeImages})
    }
  }
}
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/_mixins.scss";
@import "@/styles/_buttons.scss";

.property-modal {
  bottom: 0;
  left: 0;
  pointer-events: auto;
  position: absolute;
  right: 32.5rem;
  top: 0;
  z-index: 100;

  .btn {
    margin-left: 1.5rem;
  }

  &__mask {
    background: rgba(0, 0, 0, 0.6);
    height: 100%;
    pointer-events: auto;
    position: absolute;
    width: 100%;
  }

  &__builder-logo {
    background: white;
    border: 1px solid black;
    margin-bottom: 1rem;
    padding: 1rem;
    width: 100%;
  }

  &__inner {
    border-radius: .5rem;
    bottom: 20rem;
    display: flex;
    flex-direction: column;
    left: 50%;
    pointer-events: auto;
    position: absolute;
    transform: translateX(-50%);
    width: 80rem;

    .has-features & {
      left: calc(50% - 10rem);
    }
  }

  &__buttons {
    align-items: center;
    display: flex;
  }

  &__note {
    bottom: 8rem;
    color: #000;
    right: 1rem;
    position: absolute;
    z-index: 2;
  }

  &__status {
    border-radius: 50%;
    height: 12rem;
    left: 13.4rem;
    position: absolute;
    top: 4rem;
    width: 12rem;
    z-index: 1;

    &--on-hold {
      background: #f38433;
      filter: drop-shadow(.8rem .8rem .75rem rgba(#000, .5));

      &::after {
        content: 'ON HOLD';
        font-size: 2.7rem;
        left: 50%;
        letter-spacing: .15rem;
        line-height: 1;
        position: absolute;
        text-align: center;
        top: 2.8rem;
        transform: translateX(-50%);
      }

      &::before {
        background: #fff;
        content: '';
        left: 50%;
        line-height: 1;
        position: absolute;
        text-align: center;
        top: 9.2rem;
        transform: translateX(-50%);
        height: .2rem;
        width: 3.8rem;
      }
    }
  }

  &__gallery {
    background: #fff;
    border-radius: .5rem .5rem 0 0;
    font-size: 0;
    height: 100%;
    overflow: hidden;
    position: relative;
    width: 100%;

    .has-features & {
      border-radius: .5rem 0 0;
    }

    .message-on-hold {
      color: $black;
      font-size: 1.4rem;
      font-weight: 700;
      left: 50%;
      position: absolute;
      text-align: center;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 46rem;
    }

    img,
    video {
      height: 100%;
      object-fit: contain;
      width: 100%;
    }
  }

  &__overlay-image {
    position: absolute;
    height: 100%;
    width: 100%;
    object-fit: contain;
    z-index: 999;
  }

  &__controls {
    align-items: center;
    bottom: 3rem;
    display: flex;
    justify-content: space-between;
    position: absolute;
    right: 2rem;

    .prev {
      margin: 0 1rem 0 0;
    }

    .next {
      margin: 0 auto 0 0;
    }

    .btn-circle {
      background: #212122;
      color: $white;
    }

    .btn-circle.inactive {
      filter: grayscale(50%);
      opacity: .25;
    }
  }

  &__features {
    background: $lightgreen;
    border-radius: 0 .5rem .5rem 0;
    flex-shrink: 0;
    padding: 4rem 2rem;
    overflow: scroll;
    pointer-events: all;
    position: relative;
    width: 25rem;
    height: 100%;
    right: -25rem;
    position: absolute;

    &::-webkit-scrollbar {
      appearance: none;
      height: 0;
      width: .75rem;
    }

    &::-webkit-scrollbar-track { 
      // background: $white;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: .5rem;
      background-color: rgba(0, 0, 0, .5);
    }

    .feature-heading {
      color: $black;
      font-size: 1.4rem;
      font-weight: 600;
      margin: 0 0 0.6rem;
      opacity: 1;
      padding: 0;
      text-transform: uppercase;
    }

    .feature-list {
      color: $black;
      padding: 0 0 2rem;
      white-space: collapse;
      
      ul {
        // list-style: none;
        padding: 0 0 0 1rem;
        margin: 0 0 2rem 0;

        li {
          line-height: 1;
          margin: .5em 0;
        }
      }
    }

    .feature {
      color: $black;
      font-size: 1.1rem;
      line-height: 1.3;
      padding: 0 0 2rem;
      word-wrap: break-word;
      // white-space: preserve;

      & ul {
        padding: 0 0 0 1em !important;
      }
    }
  }

  &__info {
    align-items: center;
    background: #212122;
    border-radius: 0 0 .5rem .5rem;
    display: flex;
    height: 7.5rem;
    overflow: hidden;
    padding: 2rem;

    .spacer {
      flex-grow: 1;
    }

    .has-features & {
      border-radius: 0 0 0 .5rem;
    }

    .detail-wrapper {
      display: flex;
      margin: 0 0 0 2rem;
      width: 100%;

      .details {
        display: flex;
      }
    }

    .detail {
      display: flex;
      flex-direction: column;
      margin: 0 2rem 0 0;

      &__label {
        color: $lime;
        font-size: 1rem;
        text-transform: uppercase;
        margin-bottom: 0rem;
      }

      &__value {
        font-size: 1.4rem;
        position: relative;
        // text-transform: uppercase;

        sup {
          position: absolute;
          top: -.2rem;
        }
      }
    }

    .facade {
      margin: auto 1rem auto auto;
    }

    .close {
      margin: auto 0 auto 1rem;
    }
  }
}

.associated-packages {
  display: flex;
  align-items: center;

  .btn {
    align-items: center;
    display: flex;
    justify-content: center;
  }
}
</style>